import { ChangeEventHandler, useRef, useState } from 'react';
import Button from '@/components/Button';
import { getImageThumbnailUrl, Image } from '@/models/Image';
import { InformationCircleIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface UploadProgress {
  uploadedSize: number;
  totalSize: number;
  percentage: number;
}

export type ProgressCallback = (progress: UploadProgress) => void;

interface ImageUploadFormProps {
  value?: Image[];
  onUpload: (file: File, index: number, progressCallback: ProgressCallback) => Promise<Image>;
  onDelete: (index: number) => Promise<void>;
  autoUpload?: boolean;
}

interface ImageUploadState {
  images: Image[];
  files: (File | undefined)[];
  progress: UploadProgress[];
  isUploading: boolean;
}

const ImageUploadForm = ({
  value = [],
  onUpload,
  onDelete,
  autoUpload = false,
}: ImageUploadFormProps) => {
  const [state, setState] = useState<ImageUploadState>({
    images: value || [],
    files: [],
    progress: [],
    isUploading: false,
  });

  const fileInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const uploadFileAtIndex = async (file: File, i: number) => {
    setState((prevState) => ({ ...prevState, isUploading: true }));

    try {
      const progressCallback: ProgressCallback = (progress) => {
        setState((prev) => {
          const updated = [...prev.progress];
          updated[i] = progress;
          return { ...prev, progress: updated };
        });
      };

      const image = await onUpload(file, i, progressCallback);

      setState((prevState) => {
        const updatedFiles = [...prevState.files];
        updatedFiles[i] = undefined;
        return {
          ...prevState,
          files: updatedFiles,
          images: [...prevState.images, image],
          progress: prevState.progress.filter((_, idx) => idx !== i),
        };
      });
    } catch (err) {
      console.error('Upload failed for file at index', i, err);
    }

    setState((prev) => ({ ...prev, isUploading: false }));
  };

  const handleFileChange =
    (index: number): ChangeEventHandler<HTMLInputElement> =>
    async (event) => {
      const files = event.target.files;
      if (files && files[0]) {
        const file = files[0];
        setState((prevState) => {
          const newFiles = [...prevState.files];
          newFiles[index] = file;
          return { ...prevState, files: newFiles };
        });

        if (autoUpload) {
          await uploadFileAtIndex(file, index);
        }
      }
      event.target.value = '';
    };

  const handleUpload = async (): Promise<void> => {
    setState((prevState) => ({ ...prevState, progress: [], isUploading: true }));

    for (let i = 0; i < state.files.length; i++) {
      const file = state.files[i];
      if (!file) {
        continue;
      }

      await uploadFileAtIndex(file, i);
    }

    setState((prev) => ({ ...prev, isUploading: false }));
  };

  const handleDeleteFile = (index: number): void => {
    setState((prevState) => {
      const updatedFiles = [...prevState.files];
      updatedFiles.splice(index, 1);
      return { ...prevState, files: updatedFiles };
    });
  };

  const handleDeleteImage = async (index: number): Promise<void> => {
    await onDelete(index);
    setState((prevState) => {
      const updated = prevState.images.filter((img) => img.number !== index + 1);
      return { ...prevState, images: updated };
    });
  };

  const { files, images, isUploading } = state;

  return (
    <div>
      <div className="flex gap-4">
        {Array.from({ length: 3 }).map((_, i) => {
          const image = images.find((img) => img.number === i + 1);
          const file = files[i];

          if (image) {
            return (
              <div
                key={`image-${image.number}`}
                className="relative rounded-lg border border-gray-300 items-center justify-center p-4 shadow-md"
                data-testid={`image-upload-image-${image.number}`}
              >
                <button
                  onClick={() => handleDeleteImage(i)}
                  type="button"
                  data-testid={`image-upload-delete-${image.number}`}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <div className="flex items-center justify-center h-full">
                  <img
                    src={getImageThumbnailUrl(image)}
                    alt=""
                    style={{ width: '100px', height: 'auto' }}
                  />
                </div>
              </div>
            );
          }

          if (file) {
            const url = URL.createObjectURL(file);
            return (
              <div
                key={`file-${i}`}
                className="relative rounded-lg border border-gray-300 items-center justify-center p-4 shadow-md overflow-hidden"
                data-testid={`image-upload-file-${i + 1}`}
              >
                <button
                  onClick={() => handleDeleteFile(i)}
                  type="button"
                  className={`absolute top-1 right-1 p-1 text-white rounded-full focus:outline-none focus:ring ${
                    isUploading
                      ? 'bg-gray-300 hover:bg-gray-400 focus:ring-gray-200'
                      : 'bg-red-500 hover:bg-red-600 focus:ring-red-200'
                  }`}
                  disabled={isUploading}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <img src={url} alt="" style={{ width: '100px', height: 'auto' }} />
                </div>

                {isUploading && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                    <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            );
          }

          return (
            <div
              key={`placeholder-${i}`}
              data-testid={`image-upload-slot-${i + 1}`}
              className="group flex items-center justify-center w-[100px] h-[100px] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors duration-300 ease-in-out"
              onClick={() => fileInputsRef.current[i]?.click()}
            >
              <PlusIcon className="h-6 w-6 text-gray-400 group-hover:text-gray-400 transition-colors duration-300 ease-in-out" />

              <input
                ref={(ref) => (fileInputsRef.current[i] = ref)}
                type="file"
                data-testid={`image-upload-input-${i + 1}`}
                onChange={handleFileChange(i)}
                className="hidden"
                accept="image/jpeg,image/png,image/gif,image/webp"
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-1 mt-4 text-gray-500 text-sm">
        <InformationCircleIcon className="h-4 w-4" />
        <span>Allowed file types: JPEG, PNG, GIF, WebP</span>
      </div>

      {!autoUpload && (
        <div className="flex gap-4 mt-8">
          {files.some((f) => f) && (
            <Button variant="primary" onClick={handleUpload} disabled={isUploading}>
              Upload Images
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUploadForm;
